import {
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";

import { usePayableQuery } from "../../../../Invoices/screens/payables/PayableDetails";
import { useQueryEngagement } from "../../../../query/engagements/queries/useQueryEngagement";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { useQueryPayeeEngagement } from "../../../../query/payeeEngagements/queries/useQueryPayeeEngagement";
import { useQueryPayeeRows } from "../../../../query/search/payee/queries/useQueryPayeeRows";
import { useQueryWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import {
  useModalWorkLogHistory,
  WorkLogHistoryWidget
} from "../../components/WorkLogHistoryWidget";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getPayeeName } from "../../../Payees/selectors/getPayeeNames";
import { RateCardViewWidget } from "../../../RateCards/components/RateCardViewWidget";
import { AlertWorkLogReviewStatusPayer } from "../../components/AlertWorkLogReviewStatusPayer";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetails } from "../../components/WorkLogAdditionalDetails";
import { WorkLogAssignmentInfoPayerView } from "../../components/WorkLogAssignmentInfoPayerView";
import { WorkLogStatusStepperPayerView } from "../../components/WorkLogStatusStepperPayerView";
import { useBulkReviewWorkLogs } from "../../hooks/useBulkReviewWorkLogs";
import { useModalApproveWorkLog } from "../../modals/ModalApproveWorkLog";
import { useModalCancelWorkLogInvoice } from "../../modals/ModalCancelWorkLogInvoice";
import { useModalRejectWorkLog } from "../../modals/ModalRejectWorkLog";
import { useModalReopenWorkLog } from "../../modals/ModalReopenWorkLog";
import { useModalRequestChanges } from "../../modals/ModalRequestChanges";
import { PATH_WORK_LOGS_PAYER } from "../../paths";
import {
  canPayerApprove,
  canPayerEdit,
  canPayerReopen,
  hasPayeeRequestedEditing,
  isStatusInProgress
} from "../../utils/workLogPermissions";

export const RoutePayerWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const userId = useUserId();
  const history = useHistory();

  // Query work log
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  // Query payable to check if it's accessible
  const queryPayable = usePayableQuery(workLog?.invoiceId!, {
    enabled: !!workLog?.invoiceId,
    retry: false
  });

  // Query payee information
  const queryPayeeRow = useQueryPayeeRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payeeId = queryPayeeRow.data?.[0]?.payeeId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayee = useQueryPayee(payeeId!, {
    enabled: !!payeeId
  });

  const queryPayerPayeeEngagement = useQueryPayeeEngagement(
    payerPayeeEngagementId!,
    {
      enabled: !!payerPayeeEngagementId
    }
  );

  const payee = queryPayee.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query engagement to get work definition IDs
  const engagementId = queryWorkLog.data?.engagementId;

  const queryEngagement = useQueryEngagement(engagementId!, {
    enabled: !!engagementId
  });

  // Extract work definition IDs from engagement
  const workDefinitionIds = useMemo(() => {
    return queryEngagement.data?.workDefinitions?.map(
      wd => wd.workDefinitionId
    );
  }, [queryEngagement.data?.workDefinitions]);

  // Query work definitions using IDs filter
  const queryWorkDefinitions = useQueryWorkDefinitions(
    {
      "filter[workDefinitionId][in]": workDefinitionIds,
      sort: { name: "asc" }
    },
    {
      enabled: !!workDefinitionIds && workDefinitionIds.length > 0
    }
  );

  // Query work items
  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  // Activate filtered review sidebar when ?bulkReview=true is in URL
  useBulkReviewWorkLogs(workLogId);

  // Modals for payer actions
  const modalApprove = useModalApproveWorkLog();
  const modalReject = useModalRejectWorkLog();
  const modalRequestChanges = useModalRequestChanges();
  const modalReopen = useModalReopenWorkLog();
  const modalCancelInvoice = useModalCancelWorkLogInvoice();
  const modalWorkLogHistory = useModalWorkLogHistory();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId || !payee) {
      return "Work Log Details";
    }

    const payeeName = getPayeeName(payee);
    return payerPayeeEngagement
      ? `Work Log – ${payeeName} • ${payerPayeeEngagement.engagementName}`
      : `Work Log – ${payeeName}`;
  }, [payee, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Determine if View payable button should be disabled
  const isViewPayableDisabled = useMemo(() => {
    const invoiceId = workLog?.invoiceId;

    if (!invoiceId) {
      return true;
    }

    const isDataEmptyObject =
      !!queryPayable.data && Object.keys(queryPayable.data).length === 0;

    const isPayableNotAccessible =
      (queryPayable.isError &&
        (queryPayable.error?.status === 404 ||
          queryPayable.error?.status === 403)) ||
      isDataEmptyObject;

    return isPayableNotAccessible;
  }, [workLog?.invoiceId, queryPayable]);

  // Get contractor name for modal display
  const contractorName = payee ? getPayeeName(payee) : undefined;

  // Action handlers
  const handleViewPayable = useCallback(() => {
    if (workLog?.invoiceId) {
      history.push(`/member/invoices/payables/dashboard/${workLog.invoiceId}`);
    }
  }, [workLog, history]);

  const handleApprove = useCallback(() => {
    if (workLog) {
      modalApprove.open({
        workLog,
        contractorName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, modalApprove, queryWorkLog]);

  const handleRequestChanges = useCallback(() => {
    if (workLog) {
      modalRequestChanges.open({
        workLog,
        contractorName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, modalRequestChanges, queryWorkLog]);

  const handleReject = useCallback(() => {
    if (workLog) {
      modalReject.open({
        workLog,
        contractorName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, modalReject, queryWorkLog]);

  const handleReopenWorkLog = useCallback(() => {
    if (workLog) {
      modalReopen.open({
        workLog,
        contractorName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, modalReopen, queryWorkLog]);

  const handleCancelInvoice = useCallback(() => {
    if (workLog) {
      modalCancelInvoice.open({
        workLog,
        contractorName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, modalCancelInvoice, queryWorkLog]);

  const handleViewHistory = useCallback(() => {
    modalWorkLogHistory.open({ workLogId });
  }, [modalWorkLogHistory, workLogId]);

  // Get status-aware toolbar actions for the payer
  const getToolbarActions = () => {
    const status = workLog?.status;

    // InReview: toolbar layout depends on whether the contractor has requested editing
    if (canPayerApprove(status)) {
      const editingRequested = hasPayeeRequestedEditing(workLog as any);

      if (editingRequested) {
        // Contractor requested editing: "Return for editing" (Secondary) + "Approve" (Primary) as buttons
        return [
          {
            label: "Return for editing",
            buttonKind: "Secondary" as const,
            onClick: handleRequestChanges,
            buttonOrder: 1
          },
          {
            label: "Approve",
            buttonKind: "Primary" as const,
            onClick: handleApprove,
            buttonOrder: 2
          },
          {
            icon: "block" as const,
            label: "Reject",
            helpText: "Reject the work log and mark as not invoiced",
            destructive: true,
            onClick: handleReject
          }
        ];
      }

      // Normal InReview: "Approve" (Primary) as button,
      //                   "Return for editing" + "Reject" in three-dot menu
      return [
        {
          label: "Approve",
          buttonKind: "Primary" as const,
          onClick: handleApprove,
          buttonOrder: 1
        },
        {
          icon: "reset" as const,
          label: "Return for editing",
          helpText:
            'Send work log back to "In-progress" and ask contractor to make changes',
          onClick: handleRequestChanges
        },
        {
          icon: "block" as const,
          label: "Reject",
          helpText: "Reject the work log and mark as not invoiced",
          destructive: true,
          onClick: handleReject
        }
      ];
    }

    // Approved: "View invoice" (Secondary) as toolbar button,
    //           menu items for Reopen + Cancel
    if (canPayerReopen(status)) {
      return [
        {
          label: "View invoice",
          buttonKind: "Secondary" as const,
          onClick: handleViewPayable,
          disabled: isViewPayableDisabled,
          buttonOrder: 1
        },
        {
          icon: "reset" as const,
          label: "Reopen for changes",
          helpText:
            "Cancels the unpaid invoice and reopens work log for changes",
          onClick: handleReopenWorkLog
        },
        ...(workLog?.invoiceId
          ? [
              {
                icon: "block" as const,
                label: "Cancel invoice",
                helpText: "Cancels the invoice and the related work log",
                destructive: true,
                onClick: handleCancelInvoice
              }
            ]
          : [])
      ];
    }

    return [];
  };

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(PATH_WORK_LOGS_PAYER)
              }
            ]
          }}
          description="The work log you're looking for doesn't exist or has been deleted."
          title="Work log not found"
          type="search"
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(PATH_WORK_LOGS_PAYER)
                }
              ]
            }}
            description="You don't have permission to view this work log."
            title="Access denied"
            type="error"
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        actions={getToolbarActions()}
        breadcrumbs={[
          {
            label: "Work logs",
            onClick: () => history.push(PATH_WORK_LOGS_PAYER)
          },
          {
            label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
          }
        ]}
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
      />

      <AlertWorkLogReviewStatusPayer
        workLog={workLog}
        contractorName={contractorName}
        onRequestChanges={
          canPayerApprove(workLog?.status) ? handleRequestChanges : undefined
        }
        onViewHistory={handleViewHistory}
      />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayerView
              payee={payee}
              payerPayeeEngagement={payerPayeeEngagement}
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
            />

            <CardWorkItems
              contractorName={contractorName}
              currentUserRole="payer"
              noEdit={true}
              overlay={isStatusInProgress(workLog?.status)}
              otherPartyName={contractorName}
              payerEdit={canPayerEdit(workLog?.status)}
              payerId={userId!}
              payerPayeeEngagementId={workLog?.payerPayeeEngagementId!}
              resources={{ payerPayeeEngagement }}
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryEngagement.isInitialLoading ||
                (!!workDefinitionIds &&
                  workDefinitionIds.length > 0 &&
                  queryWorkDefinitions.isInitialLoading)
              }
              statusPill={
                isStatusInProgress(workLog?.status)
                  ? {
                      theme: "violet",
                      type: "light",
                      icon: "lock" as const,
                      text: "Returned for editing"
                    }
                  : undefined
              }
              workDefinitions={queryWorkDefinitions.data || []}
              workItems={queryWorkItems.data || []}
              workLogId={workLogId}
              onViewHistory={handleViewHistory}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayerView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <RateCardViewWidget
              context="contractor-assignment"
              payeeEngagementId={payerPayeeEngagementId}
              payeeId={payeeId}
            />
            <WorkLogAdditionalDetails
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
              overlay={isStatusInProgress(workLog?.status)}
            />
            <WorkLogHistoryWidget workLogId={workLogId} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
